import {
  type ScaleBand,
  scaleLinear,
  type ScaleLinear,
  scaleLog,
  type ScaleLogarithmic,
} from 'd3-scale';

import { replaceZeroWithEpsilon } from './replace-zero.js';
import { MinMax } from '../types/min-max.js';
import { AxisScaleType } from '../types/scales.js';

/** Reverse mapping of pixel coordinates to the Date associated to a band */
export function scaleBandInvert(scale: ScaleBand<string>) {
  const domain = scale.domain();
  const paddingOuter = scale(domain[0]) ?? 0;
  const numberOfBands = scale.step();

  return function (mousePosition: number) {
    const halfInnerPadding = (scale.step() * scale.paddingInner()) / 2;
    const index = Math.floor(
      (mousePosition + halfInnerPadding - paddingOuter) / numberOfBands,
    );
    return domain[Math.max(0, Math.min(index, domain.length - 1))];
  };
}

export const scaleBandRangeInvert = (scale: ScaleBand<string>) => {
  const domain = scale.domain();
  const paddingOuter = scale(domain[0]) ?? 0;
  const numberOfBands = scale.step();

  return function (start: number, end: number) {
    const [rangeStart, rangeEnd] = start < end ? [start, end] : [end, start];
    const halfInnerPadding = (scale.step() * scale.paddingInner()) / 2;
    const firstIndex = Math.ceil(
      (rangeStart + halfInnerPadding - paddingOuter) / numberOfBands,
    );
    // Math.ceil is used to ensure that the last band is included
    const lastIndex = Math.floor(
      (Math.ceil(rangeEnd) + halfInnerPadding - paddingOuter) / numberOfBands,
    );
    const clampedFirst = Math.max(0, Math.min(firstIndex, domain.length - 1));
    const clampedLast = Math.max(0, Math.min(lastIndex, domain.length));

    if (clampedFirst > clampedLast) {
      return [];
    }
    return domain.slice(clampedFirst, clampedLast);
  };
};

export const getLinearScale = (
  domainMin: number,
  domainMax: number,
  range?: number[],
  options = { isMinOrMaxNumber: false },
): ScaleLinear<number, number> => {
  const { isMinOrMaxNumber } = options;
  const domain = [domainMin, domainMax];

  let scale = scaleLinear().domain(domain);

  if (range) {
    scale = scale.range(range);
  }

  if (!isMinOrMaxNumber) {
    scale = scale.nice();
  }

  return scale;
};

const sanitizeLogDomain = (domainMin: number, domainMax: number) => {
  // APM-374658: scaleLog that passes through zero is not supported
  // ScaleLog(0) returns NaN and crashes charting
  let newMin = replaceZeroWithEpsilon(domainMin),
    newMax = replaceZeroWithEpsilon(domainMax);
  if (Math.sign(domainMin) !== Math.sign(domainMax)) {
    newMin = Math.abs(newMin);
    newMax = Math.abs(newMax);
  }

  return { newMin, newMax };
};

/**
 * Simple method to build a Log scale based on params.
 *
 * Note: Logarithmic scale has some restrictions, to avoid crashes, boundaries
 * will be sanitized.
 *
 * @param domainMin -
 * @param domainMax -
 * @param range -
 * @param options -
 */
export const getLogScale = (
  domainMin: number,
  domainMax: number,
  range?: number[],
  options = { isMinOrMaxNumber: false },
): ScaleLogarithmic<number, number> => {
  const { isMinOrMaxNumber } = options;
  const { newMin, newMax } = sanitizeLogDomain(domainMin, domainMax);

  let scale = scaleLog().domain([newMin, newMax]);

  if (range) {
    scale = scale.range(range);
  }

  if (!isMinOrMaxNumber) {
    scale = scale.nice();
  }

  return scale;
};

/**
 * Core method to get a Scale based on boundaries and range.
 *
 * @param scale - Scale Type will be defaulted to 'linear'
 * @param min - domain min
 * @param max - domain max
 * @param range - range boundaries [min, max]
 * @param options - possible set of options that can be passed down to scale functions
 * - isMinOrMaxNumber: boolean - should be set to true when the min or max has been set an specific number by the user.
 * @returns configured log | linear scale.
 */
export function getScale(
  scale: AxisScaleType,
  { min, max }: MinMax,
  range?: number[],
  options?: { isMinOrMaxNumber: boolean },
): ScaleLinear<number, number> | ScaleLogarithmic<number, number> {
  const scaleFunction = scale === 'linear' ? getLinearScale : getLogScale;

  return scaleFunction(min, max, range, options);
}
